import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import type { ApplicantParticulars, GetApplicantParticulars } from './types';
import { getOptionsLabel } from '../../../../../Monitor/components/getOptionsLabel';
import {
  type STUDY_MODE,
  STUDY_MODE_DISPLAY_MAP,
} from '../../../../../Application/components/ApplicationForm';
import { PAGE_TEXT } from '../../../../../Nomination/components/lang';
import {
  GENDER,
  identityDocumentTypeOptions,
} from '../../../../../Signup/components';
import {
  formatDateStringAccurate,
  formatDateStringMon,
  processRawData,
} from '../../../../../../utils';
import {
  type FundingPanel,
  FundingSource,
} from '../../../../../Nomination/components';

const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

const GET_APPLICANT_PARTICULAR = gql`
  query ($id: ID!, $filter: NominationFilter!) {
    application(id: $id) {
      _id
      applicationNo
      applicant {
        name {
          givenName
          surname
          chineseName
        }
        nationality
        email
        mobileNumber
        telephoneNumber
        identity {
          identityType
          identityDocumentType
          identityDocumentNumber
          isLocal
        }
        addressCountryRegion
        addressLine1
        addressLine2
        addressLine3
        disabilitySpecial {
          disabilitySpecialType
          disabilitySpecialDetail
        }
        dateOfBirth
        gender
        updatedAt
        createdAt
      }
      programmeChoice {
        programme
        researchArea
        department
        proposedChiefSupervisor
        studyMode
        entryYear
      }

      hkpfsInformation {
        HKPFSReferenceNumber
        validHKPFSApplication
      }
    }

    programmeChoiceMetadata {
      countryList {
        value
        label
      }

      departments {
        value
        label
      }
    }

    nominations(filter: $filter) {
      _id
      application {
        _id
      }
      admission {
        programme
      }
      fundings {
        schema
        source
      }
      scholarshipNomination {
        subjectClassification
      }
      supervisors {
        givenName
        surname
        email
      }
      submission {
        submitted
      }
    }
  }
`;

const NA = PAGE_TEXT.NA;
export const useGetApplicantParticulars = (
  id: string,
  skip?: boolean,
): GetApplicantParticulars => {
  const { data, loading, error } = useQuery(GET_APPLICANT_PARTICULAR, {
    variables: {
      id,
      filter: { application: { _id: { eq: id } } },
    },
    fetchPolicy: 'network-only',
    skip: !id || skip,
  });

  const countryList = data?.programmeChoiceMetadata?.countryList ?? [];
  const departmentList = data?.programmeChoiceMetadata?.departments ?? [];

  const nomination = data?.nominations?.[0] ?? {};
  const { fundings = [], supervisors } = nomination;

  const panel =
    (data?.nominations?.[0]?.scholarshipNomination
      ?.subjectClassification as FundingPanel) ?? NA;

  const recommendForHKPFS = fundings?.some(
    (funding: any) =>
      funding?.source === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
  );

  const { givenName, surname, email } = supervisors?.[0] ?? {};

  const { applicationNo, applicant, programmeChoice, hkpfsInformation } =
    data?.application ?? {};

  const identityDocumentType = getOptionsLabel(
    identityDocumentTypeOptions,
    applicant?.identity?.identityDocumentType,
  );

  const applicantParticulars: ApplicantParticulars = {
    nos: {
      applicationNo: applicationNo ?? '',
      HKPFSRefNo: hkpfsInformation?.HKPFSReferenceNumber ?? '-',
      HKPFSRefNoValidation: hkpfsInformation?.validHKPFSApplication ?? null,
    },
    personalInfo: {
      surName: applicant?.name.surname,
      firstName: applicant?.name.givenName,
      chineseName:
        !applicant?.name.chineseName || applicant?.name.chineseName === ''
          ? NA
          : applicant?.name.chineseName,
      nationality: getOptionsLabel(countryList, applicant?.nationality),
      email: applicant?.email ?? NA,
      mobileNumber: (applicant?.mobileNumber as boolean)
        ? applicant?.mobileNumber
        : NA,
      telephoneNumber: (applicant?.telephoneNumber as boolean)
        ? applicant?.telephoneNumber
        : NA,
      identityDocumentType,
      identityDocumentNumber: applicant?.identity?.identityDocumentNumber,
      address: `${applicant?.addressCountryRegion} ${applicant?.addressLine1} ${applicant?.addressLine2} ${applicant?.addressLine3}`,
      residenceType: processRawData(
        applicant?.identity?.identityType,
      ).toUpperCase(),
      localIndicator: applicant?.identity?.isLocal ? 'Yes' : 'No',
      disability:
        applicant?.disabilitySpecial?.disabilitySpecialType === 'NA'
          ? NA
          : (applicant?.disabilitySpecial?.disabilitySpecialType ?? NA),
      disabilityRemark: (applicant?.disabilitySpecial
        ?.disabilitySpecialDetail as boolean)
        ? applicant?.disabilitySpecial?.disabilitySpecialDetail
        : NA,
      birth:
        formatDateStringMon(applicant?.dateOfBirth, false)?.toString() ?? NA,
      gender: applicant?.gender === GENDER.FEMALE ? 'Female' : 'Male',
      updatedAt:
        formatDateStringAccurate(applicant?.updatedAt)?.toString() ?? NA,
      createdAt:
        formatDateStringAccurate(applicant?.createdAt)?.toString() ?? NA,
    },
    programmeInfo: {
      department: getOptionsLabel(departmentList, programmeChoice?.department),
      researchArea: programmeChoice?.researchArea,
      studyMode:
        STUDY_MODE_DISPLAY_MAP?.[programmeChoice?.studyMode as STUDY_MODE],
      entryYear: programmeChoice?.entryYear ?? NA,
    },
    nominationInfo: {
      panel,
      fundingSchemes:
        (fundings ?? [])?.length === 0
          ? [NA]
          : fundings?.map(
              (funding: any) =>
                fundingSourceValues[
                  funding?.source as keyof typeof fundingSourceValues
                ],
            ),
      chiefSupervisorInfo: {
        proposedChiefSupervisor: (givenName as boolean)
          ? `${surname}` + ' ' + `${givenName}`
          : '-',
        chiefSupervisorMail: email ?? NA,
      },
      recommendForHKPFS,
    },
  };

  return { applicantParticulars, loading, error };
};
